import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Visibility as EyeIcon,
  ArrowBack as BackIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Description as IntroIcon,
  Language as WebIcon,
  DesktopWindows as DesktopIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { workshopService } from '../../services/workshop.service';
import { stepService } from '../../services/step.service';
import { WorkshopDetail, Step, StepType } from '../../types/workshop.types';
import { useNotification } from '../../context/NotificationContext';

const stepTypeLabels: Record<StepType, { label: string; icon: React.ReactElement }> = {
  introduction: { label: 'Introduction', icon: <IntroIcon /> },
  web_app: { label: 'Web Application', icon: <WebIcon /> },
  remote_desktop: { label: 'Remote Desktop', icon: <DesktopIcon /> },
};

const WorkshopPreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;

  const [workshop, setWorkshop] = useState<WorkshopDetail | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);

  useEffect(() => {
    if (id) loadData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const [ws, st] = await Promise.all([
        workshopService.getWorkshop(id!),
        stepService.getSteps(id!),
      ]);
      setWorkshop(ws);
      setSteps(st);
    } catch {
      showNotificationRef.current('Failed to load workshop', 'error');
      navigate('/instructor');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setPublishDialogOpen(false);
    try {
      await workshopService.updateWorkshop(id!, { status: 'published' });
      showNotification('Tech Eval published!', 'success');
      await loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to publish tech eval';
      showNotification(msg, 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!workshop) return null;

  const step = steps[currentStep];
  const isDraft = workshop.status === 'draft';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Purple Gradient Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e1e2e 0%, #2d2b55 100%)',
          color: 'white',
          px: 4,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EyeIcon />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Preview Mode
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Viewing: {workshop.title}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
            startIcon={<BackIcon />}
            onClick={() => navigate(`/instructor/workshop/${id}/edit`)}
          >
            Back to Editor
          </Button>
          {isDraft && (
            <Button
              variant="contained"
              sx={{ backgroundColor: 'white', color: 'primary.main', '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' } }}
              onClick={() => setPublishDialogOpen(true)}
            >
              Publish Tech Eval
            </Button>
          )}
        </Box>
      </Box>

      {/* Info Banner */}
      <Box sx={{ px: 4, py: 2 }}>
        <Alert
          severity="info"
          sx={{ borderLeft: '4px solid', borderLeftColor: 'primary.main' }}
        >
          This is how students will see your tech eval. Navigate through the steps using the controls below.
        </Alert>
      </Box>

      {/* Preview Content */}
      <Box sx={{ px: 4, pb: 4 }}>
        {steps.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No steps in this tech eval yet. Go back to the editor to add steps.
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Browser Chrome */}
            <Box sx={{ backgroundColor: 'divider', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ff5f56' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ffbd2e' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#27ca41' }} />
              <Box sx={{ flex: 1, mx: 2, px: 2, py: 0.5, backgroundColor: 'white', borderRadius: 1, fontSize: 12, color: 'text.secondary' }}>
                praxis.example.com/workshop/{id}
              </Box>
            </Box>

            {/* Student View */}
            {step && (
              <Box sx={{ display: 'flex', minHeight: 500 }}>
                {/* Instructions Panel */}
                <Box
                  sx={{
                    width: step.type === 'introduction' ? '100%' : '40%',
                    p: 4,
                    borderRight: step.type !== 'introduction' ? 1 : 0,
                    borderColor: 'divider',
                    overflow: 'auto',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Chip
                      icon={stepTypeLabels[step.type]?.icon}
                      label={stepTypeLabels[step.type]?.label || step.type}
                      size="small"
                    />
                    {step.estimatedTime && (
                      <Typography variant="caption" color="text.secondary">
                        ~{step.estimatedTime} min
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {step.content}
                  </Typography>
                  {step.instructions && (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Hands-on Instructions
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {step.instructions}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Embedded App Panel (for web_app and remote_desktop) */}
                {step.type !== 'introduction' && (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fafafa' }}>
                    <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                      {step.type === 'web_app' ? <WebIcon sx={{ fontSize: 60, mb: 1 }} /> : <DesktopIcon sx={{ fontSize: 60, mb: 1 }} />}
                      <Typography variant="body1">
                        {step.type === 'web_app' ? 'Web Application' : 'Remote Desktop'}
                      </Typography>
                      {step.targetUrl ? (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          {step.targetUrl}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                          No URL configured
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        (Embedded content will render here during live sessions)
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* FM13: Publish Confirmation Dialog */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)}>
        <DialogTitle>Publish Tech Eval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Once published, this tech eval can be used in sessions. You can still edit the content, but the status cannot be reverted to draft.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} color="success" variant="contained">
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Step Navigator */}
      {steps.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderTop: 1,
            borderColor: 'divider',
            px: 4,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Button
            startIcon={<PrevIcon />}
            disabled={currentStep === 0}
            onClick={() => setCurrentStep((p) => p - 1)}
          >
            Previous
          </Button>
          <Typography variant="body2">
            Step {currentStep + 1} of {steps.length}
          </Typography>
          <Button
            endIcon={<NextIcon />}
            disabled={currentStep === steps.length - 1}
            onClick={() => setCurrentStep((p) => p + 1)}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default WorkshopPreview;
